import { useTweaks } from '../components/Tweaks.jsx';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';
import { RHero, QuoteBand, RTakeItFurther } from '../components/Resources.jsx';

const TWEAK_DEFAULTS = {
  navWelshResources: 'Welsh Resources',
  navAboutUs: 'About us',
  navContact: 'Contact',
  startLearning: 'Start Learning',

  rCrumb: 'Bedtime Welsh',
  rKicker: 'Free video series · 100 short lessons',
  rTitle: 'Bedtime Welsh.',
  rLede: "A hundred short Welsh video lessons for parents to do with their kids at bedtime. Two or three minutes each. Aran recorded them with his own children during the 2020 lockdown, so it's very much the method in its pyjamas – warm, playful, a little silly. Perfect for five minutes before lights out.",

  storyKicker: 'How it began',
  storyTitle: 'A lockdown project, still helping families four years later.',
  storyBody: 'During the first lockdown of 2020, lots of parents already learning with SSi asked us for a way to share it with their children. So Aran sat down with his own two – Angharad and Beuno – and started recording. One short video a day. Bits of the SSi method, adapted off-the-cuff for family use. The series ran for almost the whole of 2020 and turned into 100 free videos that still work beautifully for families learning together today.',

  featuredKicker: 'Start here',
  featuredTitle: 'How to use Bedtime Welsh with your children.',
  featuredBody: "Aran explains what the series is, how it works, and exactly what to do with your child – including what to do if they're shy, what to do if they're tiny, and what to do if you're the one feeling shy. Three minutes. Then press play on the playlist below.",
  featuredVideoId: 'k05bnexMKXM',
  featuredCta: 'Watch on YouTube',

  howKicker: 'What it is, in four numbers',
  howCard1Num: '2–3',
  howCard1Label: 'minutes per video',
  howCard2Num: '100',
  howCard2Label: 'videos, all free',
  howCard3Num: '1',
  howCard3Label: 'parent + 1 child',
  howCard4Num: '0',
  howCard4Label: 'Welsh needed to start',

  playlistKicker: 'The full playlist',
  playlistTitle: 'A hundred Bedtime Welsh videos, in order.',
  playlistLede: "We've cued up the second intro – Aran walks through what to actually do with your child, with examples. Everything after that is a mini-lesson of roughly three minutes. Start with one tonight, add one the next night, and one the next. That's it. That's the method.",
  playlistId: 'PLdpUXMSUG30ZWIGrSd0Aj3F4dU2J3CxrZ',
  playlistStartVideoId: 'Nlo65TSiaLE',
  playlistOpenLink: 'Open the playlist on YouTube →',

  bandKicker: 'What others are saying',
  bandTitle: 'A small encouragement, from people who\'ve been here.',
  bandQ1: "SSi is an excellent way to build confidence. It gets you speaking and really works! I'm very grateful to them.",
  bandN1: 'Leanne Wood',
  bandR1: 'Former Leader of Plaid Cymru · SSi Welsh',
  bandPhoto1: 'leanne-wood.jpg',
  bandQ2: "I'm on a four-month trip to Central America and about 95% of the Spanish I'm using here came from SSi.",
  bandN2: 'Rachel Healy',
  bandR2: 'Learner · SSi Spanish · Costa Rica',
  bandPhoto2: 'rachel-healy.png',
  bandQ3: 'The SSi approach flies in the face of established language learning theory and method – and yet, it works.',
  bandN3: 'Prof. Tess Fitzpatrick',
  bandR3: 'Head of Applied Linguistics · Swansea University',
  bandPhoto3: 'tess-fitzpatrick.png',

  rTakeKicker: 'Want the full course?',
  rTakeTitle: 'Bedtime Welsh is the family version. This is the whole method.',
  rTakeBody: 'If you want to go from a few bedtime phrases to an actual Welsh conversation, the full SSi course takes most people there in about six months. Same teacher, same method, just – you know – fewer bedtime stories.',
  rTakeCta1: 'See pricing',
  rTakeCta2: 'Try a free sample',
  rTakeQuote: 'We hope that Welsh will be one of the languages of our home for years to come — and something to pass down if we have children.',
  rTakeQuoteName: 'Jeremy',
  rTakeQuoteRole: 'SSi Welsh learner',

  footerCta: 'Start your language learning journey today.',
  footerCol1: 'What we do',
  footerCol2: 'SaySomethingin Learners',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

const GROUPS = [
  { name: 'Header', keys: ['navWelshResources','navAboutUs','navContact','startLearning'] },
  { name: 'Hero', keys: ['rCrumb','rKicker','rTitle','rLede'], long:['rLede'] },
  { name: 'How it began', keys: ['storyKicker','storyTitle','storyBody'], long:['storyBody'] },
  { name: 'Featured intro video', keys: ['featuredKicker','featuredTitle','featuredBody','featuredVideoId','featuredCta'], long:['featuredBody'] },
  { name: 'How it is (4 stats)', keys: ['howKicker','howCard1Num','howCard1Label','howCard2Num','howCard2Label','howCard3Num','howCard3Label','howCard4Num','howCard4Label'] },
  { name: 'Playlist', keys: ['playlistKicker','playlistTitle','playlistLede','playlistId','playlistStartVideoId','playlistOpenLink'], long:['playlistLede'] },
  { name: 'Quote band', keys: ['bandKicker','bandTitle','bandQ1','bandN1','bandR1','bandQ2','bandN2','bandR2','bandQ3','bandN3','bandR3'], long:['bandQ1','bandQ2','bandQ3'] },
  { name: 'Take it further', keys: ['rTakeKicker','rTakeTitle','rTakeBody','rTakeCta1','rTakeCta2','rTakeQuote','rTakeQuoteName','rTakeQuoteRole'], long:['rTakeBody','rTakeQuote'] },
  { name: 'Footer', keys: ['footerCta','footerCol1','footerCol2','footerLegal'], long:['footerLegal'] },
];

const CHIPS = [
  { key:'Format',   val:'YouTube video series' },
  { key:'Videos',   val:'100 total' },
  { key:'Length',   val:'2–3 min each' },
  { key:'Audience', val:'Parents + kids' },
];

/* --- Story band (two columns: heading + copy) --- */
const storyBand = {
  wrap: { padding:'64px 32px 20px', maxWidth:1040, margin:'0 auto' },
  grid: { display:'grid', gridTemplateColumns:'0.9fr 1.1fr', gap:64, alignItems:'start' },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:12 },
  h2: { fontFamily:'var(--font-display)', fontSize:30, lineHeight:1.2, margin:0, letterSpacing:'-.01em', textWrap:'balance' },
  body: { fontSize:16, lineHeight:1.65, color:'var(--fg-1)', margin:0, textWrap:'pretty' },
};
function StoryBand({ t }) {
  return (
    <section style={storyBand.wrap}>
      <div className="ssi-res__storyGrid" style={storyBand.grid}>
        <div>
          <div style={storyBand.kicker}>{t.storyKicker}</div>
          <h2 style={storyBand.h2}>{t.storyTitle}</h2>
        </div>
        <p style={storyBand.body}>{t.storyBody}</p>
      </div>
    </section>
  );
}

/* --- Featured intro video (large embed + copy) --- */
const feat = {
  wrap: { padding:'40px 32px 32px', maxWidth:1240, margin:'0 auto' },
  grid: { display:'grid', gridTemplateColumns:'1.4fr 1fr', gap:48, alignItems:'center' },
  videoFrame: { aspectRatio:'16/9', width:'100%', borderRadius:16, overflow:'hidden', border:'1px solid var(--border)', background:'#000', boxShadow:'0 10px 30px -14px rgba(73,3,0,.35)' },
  copy: { padding:'0 0 0 8px' },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:12 },
  h2: { fontFamily:'var(--font-display)', fontSize:32, lineHeight:1.15, margin:'0 0 18px', letterSpacing:'-.01em', textWrap:'balance' },
  body: { fontSize:16, lineHeight:1.65, color:'var(--grey)', margin:'0 0 22px', textWrap:'pretty' },
  cta: { display:'inline-flex', alignItems:'center', gap:10, color:'var(--brandRed)', textDecoration:'none', fontWeight:600, fontSize:14, padding:'10px 16px', border:'1px solid var(--brandRed)', borderRadius:999 },
  yt: { width:18, height:14 },
};
function FeaturedVideo({ t }) {
  const id = (t.featuredVideoId || '').trim();
  const pid = t.playlistId;
  // If no specific video id is set, fall back to playing the first
  // video from the playlist – YouTube picks index 0 by default.
  const src = id
    ? `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1`
    : `https://www.youtube-nocookie.com/embed/videoseries?list=${pid}&rel=0&modestbranding=1&index=1`;
  const ytHref = id
    ? `https://www.youtube.com/watch?v=${id}`
    : `https://www.youtube.com/playlist?list=${pid}`;
  return (
    <section style={feat.wrap}>
      <div className="ssi-res__featGrid" style={feat.grid}>
        <div style={feat.videoFrame}>
          <iframe
            src={src}
            title={t.featuredTitle}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            style={{width:'100%', height:'100%', border:'none', display:'block'}}
          />
        </div>
        <div style={feat.copy}>
          <div style={feat.kicker}>{t.featuredKicker}</div>
          <h2 style={feat.h2}>{t.featuredTitle}</h2>
          <p style={feat.body}>{t.featuredBody}</p>
          <a href={ytHref} target="_blank" rel="noopener noreferrer" style={feat.cta}>
            <svg viewBox="0 0 24 17" style={feat.yt} aria-hidden="true"><path d="M23.5 2.7a3 3 0 0 0-2.1-2.1C19.5 0 12 0 12 0S4.5 0 2.6.6A3 3 0 0 0 .5 2.7C0 4.6 0 8.5 0 8.5s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1c.5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8Z" fill="#C2130C"/><path d="M9.6 12.1 15.8 8.5 9.6 4.9Z" fill="#fff"/></svg>
            {t.featuredCta}
          </a>
        </div>
      </div>
    </section>
  );
}

/* --- How-it-is four-stat strip --- */
const how = {
  wrap: { padding:'40px 32px 56px', maxWidth:1240, margin:'0 auto' },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:18, textAlign:'center' },
  grid: { display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:14 },
  card: { background:'var(--offWhite, #FDF5F5)', border:'1px solid var(--border)', borderRadius:16, padding:'28px 24px', textAlign:'center' },
  num: { fontFamily:'var(--font-display)', fontSize:44, color:'var(--brandRed)', lineHeight:1, letterSpacing:'-.02em' },
  label: { fontSize:13.5, color:'var(--fg-1)', marginTop:10, letterSpacing:'.01em' },
};
function HowStrip({ t }) {
  const cards = [
    { num: t.howCard1Num, label: t.howCard1Label },
    { num: t.howCard2Num, label: t.howCard2Label },
    { num: t.howCard3Num, label: t.howCard3Label },
    { num: t.howCard4Num, label: t.howCard4Label },
  ];
  return (
    <section style={how.wrap}>
      <div style={how.kicker}>{t.howKicker}</div>
      <div className="ssi-res__howGrid" style={how.grid}>
        {cards.map((c, i) => (
          <div key={i} style={how.card}>
            <div style={how.num}>{c.num}</div>
            <div style={how.label}>{c.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* --- Playlist embed --- */
const pl = {
  wrap: { padding:'32px 32px 56px', maxWidth:1240, margin:'0 auto' },
  head: { marginBottom:24, maxWidth:700 },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  h2: { fontFamily:'var(--font-display)', fontSize:30, lineHeight:1.15, margin:'0 0 10px', letterSpacing:'-.01em' },
  lede: { fontSize:15.5, lineHeight:1.6, color:'var(--grey)', margin:'0 0 4px', textWrap:'pretty' },
  frameWrap: { position:'relative' },
  frame: { aspectRatio:'16/9', width:'100%', borderRadius:16, overflow:'hidden', border:'1px solid var(--border)', background:'#000', boxShadow:'0 10px 30px -14px rgba(73,3,0,.35)' },
  footRow: { display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:14, flexWrap:'wrap', gap:8 },
  foot: { fontSize:13, color:'var(--grey)' },
  ytLink: { display:'inline-flex', alignItems:'center', gap:8, color:'var(--brandRed)', textDecoration:'none', fontWeight:600, fontSize:13.5 },
};
function PlaylistEmbed({ t }) {
  const pid = t.playlistId;
  const vid = (t.playlistStartVideoId || '').trim();
  // Embedding a single video with a `list` parameter shows the playlist
  // side-panel (queue) inside the player. Using youtube.com (not
  // youtube-nocookie) because the panel is more reliable there.
  const src = vid
    ? `https://www.youtube.com/embed/${vid}?list=${pid}&rel=0&modestbranding=1`
    : `https://www.youtube.com/embed/videoseries?list=${pid}&rel=0&modestbranding=1`;
  const ytHref = vid
    ? `https://www.youtube.com/watch?v=${vid}&list=${pid}`
    : `https://www.youtube.com/playlist?list=${pid}`;
  return (
    <section style={pl.wrap}>
      <header style={pl.head}>
        <div style={pl.kicker}>{t.playlistKicker}</div>
        <h2 style={pl.h2}>{t.playlistTitle}</h2>
        <p style={pl.lede}>{t.playlistLede}</p>
      </header>
      <div style={pl.frameWrap}>
        <div style={pl.frame}>
          <iframe
            src={src}
            title="Bedtime Welsh playlist"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            style={{width:'100%', height:'100%', border:'none', display:'block'}}
          />
        </div>
        <div style={pl.footRow}>
          <div style={pl.foot}>100 videos in the playlist – browse them all on YouTube or tap the next-video button in the player.</div>
          <a href={ytHref} target="_blank" rel="noopener noreferrer" style={pl.ytLink}>{t.playlistOpenLink}</a>
        </div>
      </div>
    </section>
  );
}

export default function WelshBedtime() {
  const { tweaks, panel } = useTweaks({ defaults: TWEAK_DEFAULTS, groups: GROUPS });
  return (
    <div data-screen-label="Bedtime Welsh">
      <Header t={tweaks}/>
      <main>
        <RHero t={tweaks} chips={CHIPS}/>
        <StoryBand t={tweaks}/>
        <FeaturedVideo t={tweaks}/>
        <HowStrip t={tweaks}/>
        <PlaylistEmbed t={tweaks}/>
        <QuoteBand t={tweaks}/>
        <RTakeItFurther t={tweaks}/>
      </main>
      <Footer t={tweaks} hideCta={true}/>
      {panel}
    </div>
  );
}
